// src/theme.js
import { createTheme } from "@mui/material/styles";

/**
 * Design system
 * -------------
 * The light theme mirrors the "Business Hub" design template:
 *   - canvas  #EFEFF1   - panels  #FFFFFF   - hairlines #EAEAEC / #EEEEF1
 *   - ink     #18181B   - muted   #71717A   - faint     #A1A1AA
 *   - accent  #7C3AED  (+ soft fills derived as accent + alpha)
 * Dark mode mirrors the "Dark Mode" design template (cool slate + violet):
 *   - base   #0F1219   - sunken  #0B0E14   - surface #171B24   - elevated #1F2530
 *   - border #262D3A   - border-strong #333B4A
 *   - primary #F2F4F8  - secondary #A6B0C0 - tertiary #6B7688
 *   - accent  #8B5CF6  (bright #A78BFA for icons/links)
 * Both modes are driven by the same token shape so components style identically.
 */

const ACCENT = "#7C3AED";
const ACCENT_DARK = "#6D28D9";
const ACCENT_SOFT_LIGHT = "rgba(124,58,237,0.08)"; // accent @ ~8%

// Dark-mode accent (design "Accent" #8B5CF6 = rgb 139,92,246)
const ACCENT_DK = "#8B5CF6";
const ACCENT_DK_HOVER = "#7C3AED";
const ACCENT_DK_BRIGHT = "#A78BFA";
const ACCENT_SOFT_DARK = "rgba(139,92,246,0.20)";

const sharedTokens = {
  // legacy brand tokens kept for any direct consumers
  turquoise: "#5ECFFF",
  babyBlue: "#5ECFFF",
  cottonCandy: "#E328AF",
  lavender: ACCENT,
  white: "#FFFFFF",
  lightGrey: "#A1A1AA",
  steel: "#A1A1AA",
  blueberry: "#18181B",
  nightSky: "#0B0E14",
  dodgerBlue: ACCENT,
  iceCream: "#EFEFF1",
  accent: ACCENT,
  accentDark: ACCENT_DARK,
};

const paletteTokens = {
  light: {
    primary: { main: ACCENT, dark: ACCENT_DARK, contrastText: "#FFFFFF" },
    secondary: { main: sharedTokens.turquoise },
    warning: { main: "#F59E0B" },
    success: { main: "#16A34A" },
    error: { main: "#DC2626" },
    info: { main: ACCENT },
    background: { default: "#EFEFF1", paper: "#FFFFFF" },
    text: { primary: "#18181B", secondary: "#71717A", disabled: "#A1A1AA" },
    divider: "#EAEAEC",
    action: {
      hover: "rgba(124,58,237,0.06)",
      selected: ACCENT_SOFT_LIGHT,
      disabled: "#A1A1AA",
      disabledBackground: "#E4E4E7",
    },
  },
  dark: {
    primary: { main: ACCENT_DK, dark: ACCENT_DK_HOVER, contrastText: "#FFFFFF" },
    secondary: { main: "#38BDF8" },
    warning: { main: "#FBBF24" },
    success: { main: "#34D399" },
    error: { main: "#FF8A80" },
    info: { main: "#38BDF8" },
    background: { default: "#0F1219", paper: "#171B24" },
    text: { primary: "#F2F4F8", secondary: "#A6B0C0", disabled: "#6B7688" },
    divider: "#262D3A",
    action: {
      hover: "rgba(139,92,246,0.12)",
      selected: ACCENT_SOFT_DARK,
      disabled: "#6B7688",
      disabledBackground: "#333B4A",
    },
  },
};

export const createKbeanTheme = (mode = "dark") => {
  const isLight = mode === "light";

  return createTheme({
    palette: {
      mode,
      ...(isLight ? paletteTokens.light : paletteTokens.dark),
    },

    kbean: {
      tokens: sharedTokens,
      accent: isLight ? ACCENT : ACCENT_DK,
      accentDark: isLight ? ACCENT_DARK : ACCENT_DK_HOVER,
      accentBright: isLight ? ACCENT : ACCENT_DK_BRIGHT,
      accentSoft: isLight ? ACCENT_SOFT_LIGHT : ACCENT_SOFT_DARK,
      accentSoft2: isLight ? "rgba(124,58,237,0.18)" : "rgba(139,92,246,0.32)",
      shell: isLight ? "#EFEFF1" : "#0F1219",
      containerGlow: isLight
        ? "0 1px 2px rgba(0,0,0,0.04)"
        : "0 10px 26px rgba(0,0,0,0.45)",
      statCardGradients: [
        "linear-gradient(135deg, rgba(94,207,255,0.96) 0%, rgba(139,92,246,0.16) 100%)",
        "linear-gradient(135deg, rgba(139,92,246,0.96) 0%, rgba(94,207,255,0.18) 100%)",
        "linear-gradient(135deg, rgba(227,40,175,0.96) 0%, rgba(255,148,38,0.18) 100%)",
        "linear-gradient(135deg, rgba(56,226,93,0.92) 0%, rgba(94,207,255,0.18) 100%)",
      ],
      // hairline used between cards / list rows
      hairline: isLight ? "#EEEEF1" : "#262D3A",
      sidebar: isLight ? "#FFFFFF" : "#171B24",
      panel: isLight ? "#FFFFFF" : "#171B24",
      panelMuted: isLight ? "#FFFFFF" : "#171B24",
      topStrip: isLight ? "#EFEFF1" : "#0F1219",
      topbar: isLight ? "rgba(255,255,255,0.8)" : "#171B24",
    },

    shape: { borderRadius: 12 },

    typography: {
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
      fontWeightMedium: 600,
      h1: { fontWeight: 800, letterSpacing: "-0.02em" },
      h2: { fontWeight: 800, letterSpacing: "-0.02em" },
      h3: { fontWeight: 800, letterSpacing: "-0.02em" },
      h4: { fontWeight: 800, letterSpacing: "-0.01em" },
      h5: { fontWeight: 800, letterSpacing: "-0.01em" },
      h6: { fontWeight: 800, letterSpacing: "-0.01em" },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 700 },
      button: {
        textTransform: "none",
        fontWeight: 700,
        fontSize: "0.85rem",
        letterSpacing: 0,
      },
      caption: { letterSpacing: 0 },
    },

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 11,
            minHeight: 40,
            padding: "8px 18px",
            transition:
              "box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
            overflow: "hidden",
          },
          contained: {
            boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
            "&.Mui-disabled": {
              ...(isLight
                ? {
                    color: paletteTokens.light.action.disabled,
                    backgroundColor:
                      paletteTokens.light.action.disabledBackground,
                    boxShadow: "none",
                  }
                : {
                    color: paletteTokens.dark.text.secondary,
                    backgroundColor: sharedTokens.nightSky,
                    border: "1px solid rgba(255,255,255,0.14)",
                    borderColor: "rgba(255,255,255,0.14)",
                    boxShadow: "none",
                  }),
            },
          },
          containedPrimary: {
            color: "#fff",
            backgroundColor: isLight ? ACCENT : ACCENT_DK,
            "&:hover": {
              backgroundColor: isLight ? ACCENT_DARK : ACCENT_DK_HOVER,
              boxShadow: "0 1px 2px rgba(0,0,0,0.16)",
            },
            "&.Mui-disabled": {
              color: isLight
                ? paletteTokens.light.action.disabled
                : paletteTokens.dark.text.secondary,
              backgroundColor: isLight
                ? paletteTokens.light.action.disabledBackground
                : sharedTokens.nightSky,
              boxShadow: "none",
            },
          },
          outlined: {
            borderColor: isLight ? "#E4E4E7" : "#333B4A",
          },
          outlinedPrimary: {
            borderWidth: 1.5,
            borderColor: isLight ? ACCENT : ACCENT_DK,
            color: isLight ? ACCENT : ACCENT_DK_BRIGHT,
            "&:hover": {
              borderWidth: 1.5,
              backgroundColor: isLight
                ? ACCENT_SOFT_LIGHT
                : "rgba(139,92,246,0.18)",
            },
          },
          text: {
            "&:hover": {
              backgroundColor: isLight
                ? "rgba(0,0,0,0.04)"
                : "rgba(255,255,255,0.06)",
            },
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            color: isLight ? "#18181B" : "#F2F4F8",
            background: isLight ? "#FFFFFF" : "#171B24",
            border: isLight ? "1px solid #EAEAEC" : "1px solid #262D3A",
            boxShadow: isLight ? "none" : "0 8px 22px rgba(0,0,0,0.45)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&.MuiCard-outlined": {
              border: isLight
                ? "1px solid #EAEAEC"
                : "1px solid #262D3A",
            },
            "&:hover": {
              boxShadow: isLight
                ? "0 4px 16px rgba(0,0,0,0.06)"
                : "0 10px 26px rgba(0,0,0,0.52)",
            },
          },
        },
      },

      MuiCardActionArea: {
        styleOverrides: {
          root: {
            ...(isLight
              ? {
                  "&:hover .MuiCardActionArea-focusHighlight": { opacity: 0 },
                }
              : {}),
          },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 20,
            "&:last-child": { paddingBottom: 20 },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 14,
            boxShadow: isLight ? undefined : "none",
          },
          outlined: {
            border: isLight
              ? "1px solid #EAEAEC"
              : "1px solid #262D3A",
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 11,
            backgroundColor: isLight ? "#FFFFFF" : "#0B0E14",
            "&:hover": {
              backgroundColor: isLight ? "#FFFFFF" : "#0B0E14",
            },
            "&.Mui-focused": {
              backgroundColor: isLight ? "#FFFFFF" : "#0B0E14",
            },
            "&.Mui-disabled": {
              backgroundColor: isLight ? "#F4F4F5" : "#0B0E14",
            },
          },
          notchedOutline: isLight
            ? { borderColor: "#E4E4E7" }
            : { borderColor: "#333B4A" },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? "#FFFFFF" : "#171B24",
            boxShadow: "none",
            borderBottom: "none",
            backdropFilter: "none",
            borderRadius: 0,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.85rem",
            minHeight: 48,
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 2.5,
            borderRadius: 3,
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: { padding: 8 },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 800,
            letterSpacing: "0.04em",
            lineHeight: 1,
          },
          sizeSmall: { height: 28 },
          sizeMedium: { height: 30 },
          label: {
            textTransform: "uppercase",
            paddingLeft: 12,
            paddingRight: 12,
            fontSize: "0.72rem",
            fontWeight: 800,
            letterSpacing: "0.04em",
            lineHeight: 1,
          },
          labelSmall: { paddingLeft: 12, paddingRight: 12 },
          labelMedium: { paddingLeft: 12, paddingRight: 12 },
          icon: { fontSize: 14, marginLeft: 10 },
          deleteIcon: { marginRight: 8 },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            maxWidth: 320,
            padding: "10px 12px",
            borderRadius: 8,
            fontSize: "0.875rem",
            fontWeight: 600,
            lineHeight: 1.45,
            letterSpacing: 0,
            color: "#FFFFFF",
            backgroundColor: isLight ? "#18181B" : sharedTokens.nightSky,
            border: isLight
              ? "1px solid rgba(255,255,255,0.16)"
              : "1px solid rgba(255,255,255,0.14)",
            boxShadow: isLight
              ? "0 12px 28px rgba(0,0,0,0.20)"
              : "0 14px 30px rgba(0,0,0,0.32)",
          },
          arrow: {
            color: isLight ? "#18181B" : sharedTokens.nightSky,
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 9,
            "&.Mui-selected": {
              backgroundColor: isLight
                ? ACCENT_SOFT_LIGHT
                : "rgba(139,92,246,0.2)",
              "&:hover": {
                backgroundColor: isLight
                  ? ACCENT_SOFT_LIGHT
                  : "rgba(139,92,246,0.26)",
              },
            },
          },
        },
      },

      MuiCssBaseline: {
        styleOverrides: isLight
          ? {}
          : {
              "*, *::before, *::after": {
                boxShadow: "none !important",
              },
              "input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, textarea:-webkit-autofill, textarea:-webkit-autofill:hover, textarea:-webkit-autofill:focus":
                {
                  WebkitTextFillColor: "#FFFFFF",
                  caretColor: "#FFFFFF",
                  WebkitBoxShadow: `0 0 0 1000px ${sharedTokens.nightSky} inset !important`,
                  boxShadow: `0 0 0 1000px ${sharedTokens.nightSky} inset !important`,
                  borderRadius: "inherit",
                  transition: "background-color 9999s ease-out 0s",
                },
            },
      },
    },
  });
};
